import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { WebsiteState } from '../store';
import { Json } from '@gift/types/Json';
import { PaintTarget } from '@gift/types/plugins/paintTargets';
import { createPaintables } from '@gift/common/helpers/createPaintables';
import { bindOptionSelectorFactory } from '@gift/common/helpers/pluginBindings';
import { selectActiveVariant } from '@gift/website-state/slices/variantsSlice';

const adapter = createEntityAdapter<Json.Variable<any>>();
const adapterSelectors = adapter.getSelectors<WebsiteState>(s => s.variables);

export const { actions, reducer } = createSlice({
    name: 'variables',
    initialState: adapter.getInitialState(),
    reducers: {
        setAll: adapter.setAll
    }
});

export const selectVariableById = adapterSelectors.selectById;

export const selectPluginIdByVariableId = createSelector(
    adapterSelectors.selectById,
    p => p?.pluginId);

export const selectNameByVariableId = createSelector(
    adapterSelectors.selectById,
    p => p?.name);

export const selectViewboxByVariableId = createSelector(
    adapterSelectors.selectById,
    p => p ? { width: p.width, height: p.height } : undefined);

export const selectAllVariables = createSelector(
    (state: WebsiteState) => state.variables,
    (variables) => variables.ids
        .map(id => variables.entities[id]!));

export const selectNonPinnedVariables = createSelector(
    selectAllVariables,
    (variables) => variables.filter(v => !v.pinned));

export const selectPinnedVariables = createSelector(
    selectAllVariables,
    (variables) => variables.filter(v => v.pinned));

export const selectVariablePaintables = createSelector([
    (state: WebsiteState) => selectAllVariables(state),
    (state: WebsiteState) => state.inputs.entities,
    (state: WebsiteState) => selectActiveVariant(state),
    (state: WebsiteState, target: PaintTarget) => target
], createPaintables);

export const createOptionBindings = <TOptions>() => ({
    createOptionSelector: bindOptionSelectorFactory(adapterSelectors).to<TOptions>()
});